import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UiStateManagementService } from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { DatePicker, Dropdown } from '@com-delta-omnichannel/mach-core-js';
import { WidgetEventService } from '../../../shared/utils/widget-event.service';
import { CarsWidgetFormType } from '../../models/cars-widget-form.interfaces';
import { CarsWidget } from '../../models/cars-widget.interface';
import { CarsErrorHandlingService } from '../../services/cars-error-handling.service';
import { CarsFormValidationService } from '../../services/cars-form-validation.service';

@Component({
  selector: 'idp-cars-widget-super',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cars-widget-super.component.html',
  styleUrls: []
})
export class CarsWidgetSuperComponent {
  pickUpDateInstance!: DatePicker;
  pickUpTimeDropdown!: Dropdown;
  dropOffDateInstance!: DatePicker;
  dropOffTimeDropdown!: Dropdown;
  expandView = false;
  isIntentionalSubmit = false;
  isSubmitted = false;
  isPickupDatePickerOpen = false;
  isDropoffDatePickerOpen = false;
  locationErrorMessage = '';
  pickupDateErrorMessage = '';
  dropoffDateErrorMessage = '';
  dropoffTimeErrorMessage = '';
  pickupTimeErrorMessage = '';
  errorCount = 0;
  firstErrorElement!: HTMLElement | null;
  expandEmit = new EventEmitter<boolean>();
  uiStateService = inject(UiStateManagementService);
  errorHandlingService = inject(CarsErrorHandlingService);
  validationService = inject(CarsFormValidationService);
  eventService = inject(WidgetEventService);

  clearLocationError(): void {
    this.locationErrorMessage = '';
    this.updateErrorCount();
  }

  updateErrorCount(): void {
    this.errorCount = [
      this.locationErrorMessage,
      this.pickupDateErrorMessage,
      this.pickupTimeErrorMessage,
      this.dropoffDateErrorMessage,
      this.dropoffTimeErrorMessage
    ].filter((error) => error && error.trim() !== '').length;
  }

  protected initializePickupControls(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): void {
    this.pickUpDateInstance = new DatePicker(content.pickUpDate);
    this.pickUpDateInstance.onChange((dateRange) => {
      if (form && dateRange?.start) {
        form.patchValue({ pickUpDate: dateRange.start });
        if (this.isSubmitted) {
          this.clearPickupDateError();
          this.validateAndShowDropoffError(form, content);
        }
      }
    });

    this.pickUpDateInstance.onClose(() => {
      this.isPickupDatePickerOpen = false;
      const triggerId = content.pickUpDate.triggerElementId;
      if (triggerId) {
        document.getElementById(triggerId)?.focus();
      }
    });

    this.pickUpTimeDropdown = new Dropdown(content.pickUpTimeDropdown);
    this.pickUpTimeDropdown.onChange((item) => {
      if (form && item?.value) {
        form.patchValue({ pickUpTime: item.value });
        if (this.isSubmitted) {
          this.validateAndShowDropoffError(form, content);
        }
      }
    });
    this.pickUpTimeDropdown.setValue('10:00');
  }

  protected initializeDropoffControls(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): void {
    this.dropOffDateInstance = new DatePicker(content.dropOffDate);
    this.dropOffDateInstance.onChange((dateRange) => {
      if (form && dateRange?.start) {
        form.patchValue({ dropOffDate: dateRange.start });
        if (this.isSubmitted) {
          this.validateAndShowDropoffError(form, content);
        }
      }
    });

    this.dropOffDateInstance.onClose(() => {
      this.isDropoffDatePickerOpen = false;
      const triggerId = content.dropOffDate.triggerElementId;
      if (triggerId) {
        document.getElementById(triggerId)?.focus();
      }
    });

    this.dropOffTimeDropdown = new Dropdown(content.dropOffTimeDropdown);
    this.dropOffTimeDropdown.onChange((item) => {
      if (form && item?.value) {
        form.patchValue({ dropOffTime: item.value });
        if (this.isSubmitted) {
          this.validateAndShowDropoffError(form, content);
        }
      }
    });
    this.dropOffTimeDropdown.setValue('10:00');
  }

  protected clearPickupDateError(): void {
    this.pickupDateErrorMessage = '';
    this.updateErrorCount();
  }

  protected validateAndShowDropoffError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): void {
    const errorMessage = this.validationService.setDropoffDateError(
      form,
      content
    );
    this.dropoffDateErrorMessage = errorMessage;
    const timeErrorMessage = this.validationService.setDropoffTimeError(
      form,
      content
    );
    this.dropoffTimeErrorMessage = timeErrorMessage;
    this.updateErrorCount();
  }
}
